'use client'

import Link from 'next/link'
import { useState } from 'react'
import { useModal } from '../context/ModalContext'
import { useAuth } from '../context/AuthContext'
import LoginModal from '../context/modals/Login'
import Searchbar from './Searchbar'
import { ChevronDown } from 'lucide-react'
import { signOut } from '@/lib/auth-client'
import DonationPopin from '../account/DonationPopin'

interface BurgerMenuProps {
	isOpen: boolean
	onClose: () => void
	links: any
}

export default function BurgerMenu({ isOpen, onClose, links }: BurgerMenuProps) {
	const { openModal, closeModal } = useModal()
	const { user } = useAuth()
	const [expanded, setExpanded] = useState<Record<string, boolean>>({})
	const [isDonationPopinOpen, setIsDonationPopinOpen] = useState(false)

	const getUrlParameters = (link: any) => {
		return link?.type === 'tags' ? `tag=${link?.slug}` : `category=${link?.slug}`
	}

	return (
		<>
			{isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />}

			<div className={`fixed top-0 left-0 h-full w-[350px] bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 lg:hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
				<div className="p-6">
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-xl font-alt font-semibold text-gray-800">Menu</h2>
						<button onClick={onClose} className="text-gray-500 hover:text-gray-700">
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
								<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>
					<hr className="border-gray-200 mb-6" />

					<nav className="space-y-8 font-alt">
						<div className="relative">
							<Searchbar />
							<button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
								<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
									<path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
								</svg>
							</button>
						</div>
						{user ? (
							<Link href="/product/create" className="block text-lg font-regular text-gray-800 hover:text-gray-600" onClick={onClose}>
								Vendez des articles
							</Link>
						) : (
							<Link
								href="#"
								onClick={() => openModal(<LoginModal closeAction={closeModal} />)}
								className="block text-lg font-regular text-gray-800 hover:text-gray-600"
							>
								Vendez des articles
							</Link>
						)}
						<Link href="/page/contact" className="block text-lg font-regular text-gray-800 hover:text-gray-600" onClick={onClose}>
							Nous contacter
						</Link>
						<button
							onClick={() => {
								setIsDonationPopinOpen(true)
								onClose()
							}}
							className="block text-lg font-regular text-gray-800 hover:text-gray-600 w-full text-left"
						>
							Faire un don
						</button>
						{user ? (
							<Link href="/account/profile" className="block text-lg font-regular text-gray-800 hover:text-gray-600" onClick={onClose}>
								Mon compte
							</Link>
						) : (
							<button
								onClick={() => openModal(<LoginModal closeAction={closeModal} />)}
								className="block text-lg font-regular text-gray-800 hover:text-gray-600 w-full text-left"
							>
								{"Se connecter / S'inscrire"}
							</button>
						)}
						<hr className="border-gray-200 my-6" />

						{links.map((link: any, index: number) => {
							const hasSubcategories = link?.type !== 'tags' && Array.isArray(link?.subcategory) && link?.subcategory?.length > 0
							const key = link?.slug ?? String(index)
							const isOpenItem = !!expanded[key]

							return (
								<div key={key} className="mb-6">
									<div className="flex items-center justify-between mb-3">
										<Link href={`/list?${getUrlParameters(link)}`} className="text-lg font-regular text-gray-800 hover:text-gray-600" onClick={onClose}>
											{link.name}
										</Link>
										{hasSubcategories && (
											<button
												type="button"
												onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
												aria-expanded={isOpenItem}
												className="p-1 text-gray-800 hover:text-gray-600"
											>
												<ChevronDown className={`size-4 transition-transform ${isOpenItem ? 'rotate-180' : ''}`} />
											</button>
										)}
									</div>

									{hasSubcategories && isOpenItem && (
										<div className="mt-3 ml-2 space-y-3">
											{link.subcategory.map((sub: any) => (
												<Link
													key={sub?.slug}
													href={`/list?${getUrlParameters(link)}&subcategory=${sub?.slug}`}
													className="block text-base text-gray-500 hover:text-gray-900 first-letter:uppercase"
													onClick={onClose}
												>
													{sub?.name}
												</Link>
											))}
										</div>
									)}
								</div>
							)
						})}
					</nav>
				</div>
			</div>
			<DonationPopin isOpen={isDonationPopinOpen} closeAction={() => setIsDonationPopinOpen(false)} />
		</>
	)
}
